import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { FiArrowDownLeft, FiArrowUpRight, FiEdit2, FiPaperclip, FiRepeat, FiTrash2, FiX } from "react-icons/fi";
import { AppButton, IconButton } from "@/components/ui/primitives";
import { useDialogBehavior } from "@/hooks/useDialogBehavior";
import { formatFlowAmount, type AmountFlow } from "@/utils/formatters";

// Card color per flow; the badge uses the same hue as its icon color on an on-card disc.
const HERO_CARD: Record<AmountFlow, string> = {
  income: "bg-[var(--ds-card-lime)] [--hero-ink:var(--ds-card-lime)]",
  expense: "bg-[var(--ds-card-flame)] [--hero-ink:var(--ds-card-flame)]",
  transfer: "bg-[var(--ds-card-lilac)] [--hero-ink:var(--ds-card-lilac)]",
};

const HERO_ICON: Record<AmountFlow, typeof FiArrowUpRight> = {
  income: FiArrowUpRight,
  expense: FiArrowDownLeft,
  transfer: FiRepeat,
};

export interface TransactionDetailSheetProps {
  open: boolean;
  onClose: () => void;
  /** Título del diálogo (h2), p. ej. "Movimiento". */
  title: string;
  closeLabel: string;
  hero: { label: string; amount: number; flow: AmountFlow; name: string };
  facts: { label: string; value: ReactNode }[];
  factsLabel: string;
  editLabel: string;
  deleteLabel: string;
  onEdit: () => void;
  onDelete: () => void;
}

/** Valor de un dato en la hoja: categoría con color (relleno) o tag (contorno). */
/** Comprobante adjunto: enlace si hay `href`, texto con icono si no. */
export function DetailFile({ children, href }: { children: ReactNode; href?: string }) {
  const content = (
    <>
      <FiPaperclip size={18} aria-hidden="true" />
      {children}
    </>
  );
  const className = "inline-flex items-center gap-2 text-[var(--app-text)] underline-offset-4 hover:underline";
  return href ? <a href={href} className={`${className} app-focus-ring rounded-sm`} target="_blank" rel="noreferrer">{content}</a> : <span className={className}>{content}</span>;
}

/** Panel lateral de detalle de un movimiento: 520px en md+, pantalla completa debajo. */
export function TransactionDetailSheet({
  open,
  onClose,
  title,
  closeLabel,
  hero,
  facts,
  factsLabel,
  editLabel,
  deleteLabel,
  onEdit,
  onDelete,
}: TransactionDetailSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDialogBehavior({ open, onClose, containerRef: panelRef });

  if (!open) return null;

  const HeroIcon = HERO_ICON[hero.flow];

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[var(--app-overlay)] backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative flex h-full w-full flex-col gap-6 overflow-y-auto border-[var(--app-border)] bg-[var(--app-surface)] p-6 text-[var(--app-text)] shadow-[var(--app-shadow-strong)] md:w-[520px] md:rounded-l-[var(--ds-radius-xl)] md:border-l"
      >
        <header className="flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-xl font-medium leading-7 tracking-[-0.02em]">{title}</h2>
          <IconButton tone="secondary" size="sm" aria-label={closeLabel} onClick={onClose}>
            <FiX size={20} aria-hidden="true" />
          </IconButton>
        </header>

        <section className={`rounded-[var(--ds-radius-lg)] p-6 text-[var(--ds-on-card)] ${HERO_CARD[hero.flow]}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase leading-4 tracking-[0.08em]">{hero.label}</span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ds-on-card)] text-[var(--hero-ink)]" aria-hidden="true">
              <HeroIcon size={20} />
            </span>
          </div>
          <p className="mt-4 text-5xl font-medium leading-none tracking-[-0.04em] tabular-nums">{formatFlowAmount(hero.amount, hero.flow)}</p>
          <h3 className="mt-2 text-xl font-medium leading-7">{hero.name}</h3>
        </section>

        <dl aria-label={factsLabel} className="m-0 rounded-[var(--ds-radius-lg)] bg-[var(--app-surface-soft)] px-4 py-1">
          {facts.map((fact) => (
            <div key={fact.label} className="flex min-h-13 items-center justify-between gap-4 border-t border-[var(--app-border)] py-3 first:border-t-0">
              <dt className="text-[15px] leading-[22px] text-[var(--app-muted-text)]">{fact.label}</dt>
              <dd className="m-0 text-right text-[15px] leading-[22px] text-[var(--app-text)]">{fact.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-auto flex gap-3">
          <AppButton tone="primary" fullWidth className="flex-1" leadingIcon={<FiEdit2 size={18} aria-hidden="true" />} onClick={onEdit}>
            {editLabel}
          </AppButton>
          <AppButton tone="dangerSoft" fullWidth className="flex-1" leadingIcon={<FiTrash2 size={18} aria-hidden="true" />} onClick={onDelete}>
            {deleteLabel}
          </AppButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
